/**
 * App-wide font-size preferences (UI-only, persisted per-device in localStorage).
 *
 * Three independent scales, applied ONLY on phone-sized screens so the desktop
 * layout is untouched:
 *  - listTitle / listItem — the trip list card heading and the checkoff item rows
 *    (driven via CSS px vars on those specific elements).
 *  - appText — a global scale for *everything else* (every other page / normal
 *    text), applied by scaling the root font size so all rem-based type and
 *    spacing bump proportionally.
 * Values are multipliers of each surface's base size.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type FontScaleKey = 'listTitle' | 'listItem' | 'appText';

/** Base sizes (px). For appText this is the root font size. */
export const FONT_BASE_PX: Record<FontScaleKey, number> = {
  listTitle: 16, // trip list card heading
  listItem: 14, // checkoff item name
  appText: 16, // root font size → scales all other rem-based text
};

/** CSS custom property the trip surfaces read their font-size from. */
export const FONT_CSS_VAR: Record<'listTitle' | 'listItem', string> = {
  listTitle: '--uc-font-list-title',
  listItem: '--uc-font-list-item',
};

export const FONT_SCALE_MIN = 0.7;
export const FONT_SCALE_MAX = 1.7;
export const FONT_SCALE_STEP = 0.1;

const DEFAULT_SCALES: Record<FontScaleKey, number> = {
  listTitle: 1,
  listItem: 1,
  appText: 1,
};

export const FONT_SCALE_KEYS = Object.keys(DEFAULT_SCALES) as FontScaleKey[];

interface FontSettingsState {
  scales: Record<FontScaleKey, number>;
  setScale: (key: FontScaleKey, value: number) => void;
  reset: () => void;
}

export const useFontSettings = create<FontSettingsState>()(
  persist(
    (set) => ({
      scales: { ...DEFAULT_SCALES },
      setScale: (key, value) => set((state) => ({ scales: { ...state.scales, [key]: value } })),
      reset: () => set({ scales: { ...DEFAULT_SCALES } }),
    }),
    {
      name: 'uc-font-settings',
      // Drop any previously-saved keys (e.g. the old itemDetail) and re-seed
      // missing ones so the shape always matches DEFAULT_SCALES.
      merge: (persisted, current) => {
        const saved =
          (persisted as { scales?: Partial<Record<FontScaleKey, number>> })?.scales ?? {};
        const scales = { ...DEFAULT_SCALES };
        for (const key of FONT_SCALE_KEYS) {
          if (typeof saved[key] === 'number') {
            scales[key] = saved[key] as number;
          }
        }
        return { ...current, scales };
      },
    },
  ),
);
